import React from 'react';
import { Button } from '@/components/ui/button';
import type { LucideIcon } from 'lucide-react';
import { config } from '@/config/config';
import { CheckIcon, XIcon } from 'lucide-react';

interface IconBtnProps {
  icon: LucideIcon;
  tooltip: string;
  onClick: () => void;
}

/**
 * Reusable round ghost button with an icon.
 */
export function IconBtn({ icon: Icon, tooltip, onClick }: IconBtnProps) {
  // Get reference to the config
  const tableConfig = config.productsTable;

  // Determine if this is a save or cancel button for special styling
  const isSaveButton = Icon === CheckIcon;
  const isCancelButton = Icon === XIcon;

  let buttonClasses =
    'h-7 w-7 rounded-full transition-all duration-200 hover:scale-105 active:scale-95 shadow-sm hover:shadow-md';

  if (isSaveButton) {
    buttonClasses +=
      ' bg-green-50 hover:bg-green-100 active:bg-green-200 border border-green-200 text-green-700 hover:border-green-300 hover:text-green-800';
  } else if (isCancelButton) {
    buttonClasses +=
      ' bg-red-50 hover:bg-red-100 active:bg-red-200 border border-red-200 text-red-700 hover:border-red-300 hover:text-red-800';
  } else {
    buttonClasses += ' hover:bg-slate-100';
  }

  return (
    <Button
      type="button"
      variant={isSaveButton || isCancelButton ? 'outline' : 'ghost'}
      size="icon"
      title={tooltip}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className={buttonClasses}
      aria-label={tooltip}
      data-cy={isSaveButton ? 'edit-product' : isCancelButton ? 'delete-product' : undefined}
    >
      <Icon className={`size-4 ${isSaveButton || isCancelButton ? '' : 'text-slate-600'}`} />
      <span className="sr-only">{tooltip}</span>
    </Button>
  );
}
